///
/// EarthBrowser
///
/// Created by Matt Giger on 10/16/2026
/// Copyright (c) 2026 Geodata Labs
///

import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba, type color } from "@stovokor/math/color"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { EnsembleTrackPoint, EnsembleTracks, StormTrackPoint } from "./HurricanesTable"
import { densifiedTrackPositions } from "./stormTrack"

// Members are deliberately faint: fifty near-coincident lines read as a
// density cloud, and the official forecast track must stay dominant
const cMemberWidth = 1
const cMemberAlpha = 0.14
const cMemberFocusedAlpha = 0.3
const cMemberColor = rgba(0.72, 0.88, 1.0, cMemberAlpha)

// The deterministic HRES run gets its own, brighter line
const cHresWidth = 1.8
const cHresAlpha = 0.55
const cHresFocusedAlpha = 0.85
const cHresColor = rgba(0.95, 0.97, 1.0, cHresAlpha)

// Coarser geodesic subdivision than the official track: these lines are
// thin and numerous, so vertex count matters more than chord smoothness
const cMemberMaxStepRad = 0.012

interface EnsembleLine {
	line: PolylineNode
	baseColor: color
	baseWidth: number
	focusedAlpha: number
}

/**
 * ECMWF ensemble "spaghetti" for one storm: every ENS member track plus the
 * HRES deterministic track, draped at the same altitude as the official
 * forecast. Brightens slightly while the storm is selected.
 */
export class StormEnsembleTracksNode extends NodeBase {
	private readonly lines: EnsembleLine[] = []
	private focused = false

	constructor(context: StovokorContext, tracks: EnsembleTracks, altitudeKm: number) {
		super()

		const runMs = Date.parse(tracks.run)

		for (const member of tracks.members) {
			this.addLine(context, member.pts, runMs, altitudeKm, cMemberColor, cMemberWidth, cMemberFocusedAlpha)
		}
		if (tracks.hres) {
			this.addLine(context, tracks.hres, runMs, altitudeKm, cHresColor, cHresWidth, cHresFocusedAlpha)
		}
	}

	/** Number of member tracks rendered */
	public get memberCount(): number {
		return this.lines.length
	}

	private addLine(
		context: StovokorContext,
		pts: EnsembleTrackPoint[],
		runMs: number,
		altitudeKm: number,
		baseColor: color,
		baseWidth: number,
		focusedAlpha: number
	): void {
		if (pts.length < 2) return

		const points: StormTrackPoint[] = pts.map(([tau, lat, lon, windKt, mslp]) => ({
			t: runMs + tau * 3_600_000,
			lat,
			lon,
			wind_kt: windKt,
			mslp_mb: mslp,
			category: 0,
			type: null,
			tau,
		}))

		const positions = densifiedTrackPositions(points, altitudeKm, cMemberMaxStepRad)
		if (positions.length < 2) return

		const line = new PolylineNode(context, {
			positions,
			width: baseWidth,
			color: baseColor,
			depthWrite: false,
			polygonOffset: cSurfaceOverlayDepthBias,
		})
		this.lines.push({ line, baseColor, baseWidth, focusedAlpha })
		this.addNode(line)
	}

	/** Brighten the ensemble while the storm is selected */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) return
		this.focused = focused

		for (const entry of this.lines) {
			const alpha = focused ? entry.focusedAlpha : entry.baseColor.a
			entry.line.updateColor(rgba(entry.baseColor.r, entry.baseColor.g, entry.baseColor.b, alpha))
			entry.line.updateWidth(focused ? entry.baseWidth + 0.5 : entry.baseWidth)
		}
	}
}
